import { AnalyticsAttributes } from '@carvana/merch-components/contracts';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

type ContextType = {
  analyticsAttributes?: AnalyticsAttributes;
  section: string | null;
  updateAnalyticsAttributes: (key: string, value: any) => void;
};

const defaultContext: ContextType = {
  analyticsAttributes: {},
  section: null,
  updateAnalyticsAttributes() {}
};

export type OwnProps = React.PropsWithChildren<{
  analyticsAttributes?: AnalyticsAttributes;
  section: string | null;
}>;

const SectionAnalyticsContext = createContext<ContextType>(defaultContext);

export const SectionAnalyticsProvider = ({
  analyticsAttributes: analyticsAttributesProps,
  section,
  children
}: OwnProps) => {
  const [analyticsAttributes, setAnalyticsAttributes] =
    useState<AnalyticsAttributes>({
      ...(analyticsAttributesProps ?? {}),
      section
    });

  const updateAnalyticsAttributes = useCallback((key: string, value: any) => {
    setAnalyticsAttributes(prevAttributes => ({
      ...prevAttributes,
      [key]: value
    }));
  }, []);

  const value = useMemo(
    () => ({
      analyticsAttributes,
      section,
      updateAnalyticsAttributes
    }),
    [analyticsAttributes, section, updateAnalyticsAttributes]
  );

  return (
    <SectionAnalyticsContext.Provider value={value}>
      {children}
    </SectionAnalyticsContext.Provider>
  );
};

SectionAnalyticsProvider.displayName = 'SectionAnalyticsProvider';

export const useSectionAnalytics = () => {
  return useContext(SectionAnalyticsContext);
};

export const useAnalyticsSection = () => {
  return useContext(SectionAnalyticsContext).section;
};

export function MockedSectionAnalyticsContextProvider({
  children,
  analyticsAttributes = {},
  section
}: React.PropsWithChildren<{
  analyticsAttributes?: AnalyticsAttributes;
  section: string;
}>) {
  return (
    <SectionAnalyticsContext.Provider
      value={{
        analyticsAttributes,
        section,
        updateAnalyticsAttributes: () => {}
      }}
    >
      {children}
    </SectionAnalyticsContext.Provider>
  );
}

export default SectionAnalyticsContext;
